import { useState, useCallback, useEffect } from 'react';
import { auth } from './supabaseClient';
import type { User as AuthUser } from '../types';
import type { AuthContext } from '../types';

export const useAuth = (): AuthContext => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Initialize auth state from session
  useEffect(() => {
    const initAuth = async () => {
      try {
        const {
          data: { session },
        } = await auth.getSession();

        if (session?.user) {
          // Fetch user profile from database or metadata
          const userData = session.user.user_metadata || {};
          setUser({
            id: session.user.id,
            email: session.user.email || '',
            name: userData.name || session.user.user_metadata?.full_name || '',
            role: userData.role || 'user',
            createdAt: new Date(session.user.created_at),
          });
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        const userData = session.user.user_metadata || {};
        setUser({
          id: session.user.id,
          email: session.user.email || '',
          name: userData.name || session.user.user_metadata?.full_name || '',
          role: userData.role || 'user',
          createdAt: new Date(session.user.created_at),
        });
      } else {
        setUser(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { error: signInError } = await auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Login failed');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { error: signOutError } = await auth.signOut();
      if (signOutError) throw signOutError;
      setUser(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Logout failed');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const signup = useCallback(
    async (email: string, password: string, name: string) => {
      setIsLoading(true);
      setError(null);
      try {
        const { error: signUpError } = await auth.signUp({
          email,
          password,
          options: {
            data: {
              name,
            },
          },
        });

        if (signUpError) throw signUpError;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Signup failed');
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { user, isLoading, error, login, logout, signup };
};
